import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  FaGithub,
  FaLinkedin,
  FaTwitter,
  FaArrowRight,
  FaArrowDown,
  FaReact,
  FaNodeJs,
  FaDocker,
  FaGitAlt,
  FaJava,
  FaFileAlt,
  FaLaptopCode,
  FaServer,
} from "react-icons/fa";
import {
  SiCsharp,
  SiPostgresql,
  SiJavascript,
  SiMongodb,
} from "react-icons/si";

// Images
import todooScreenshot from "../images/todo.jpg";
import apiScreenshot from "../images/api.jpg";
import chatAppScreenshot from "../images/chatApp.jpg";
import uninowLogo from "../images/uninow-logo.png";
import ecklebeLogo from "../images/ecklebe-logo.png";

import "../styles/home.css";
import "../styles/experience.css";

const experienceItems = [
  {
    title: "Unterstützung der IT-Abteilung @ Dr. Ecklebe",
    period: "Oct 2023 – Jan 2024, Magdeburg",
    logo: ecklebeLogo,
    points: [
      "Implemented a company application using the MAUI framework with C# and XAML.",
      "Set up structured release processes for updating Grafana in an IIoT solution with Docker.",
      "Handled deployment, testing, and comprehensive documentation to ensure smooth operations."
    ],
    icons: [
      <FaDocker className="tech-icon" key="docker" />,
      <FaServer className="tech-icon" key="server" />,
      <SiCsharp className="tech-icon" key="csharp" />
    ]
  },
  {
    title: "Werkstudent Software Entwickler @ UniNow",
    period: "Mar 2024 – Jun 2025, Magdeburg",
    logo: uninowLogo,
    points: [
      "Developing and optimizing web-scraping solutions using Node.js, Puppeteer, and Cheerio.",
      "Extracting and analyzing university data with efficient server-side HTML parsing and browser automation.",
      "Creating detailed technical documentation for maintenance and further development."
    ],
    icons: [
      <FaLaptopCode className="tech-icon" key="laptop" />,
      <FaServer className="tech-icon" key="server" />,
      <FaNodeJs className="tech-icon" key="nodejs" />
    ]
  }
];

function Home() {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [activeSection, setActiveSection] = useState("home");

  useEffect(() => {
    const handleScroll = () => {
      // Calculate scroll progress
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (window.scrollY / totalHeight) * 100;
      setScrollProgress(progress);

      // Determine active section
      const sections = ["home", "projects", "experience", "about", "contact"];
      const sectionElements = sections.map(id => document.getElementById(id));
      const scrollPosition = window.scrollY + window.innerHeight / 2;

      for (let i = sectionElements.length - 1; i >= 0; i--) {
        const element = sectionElements[i];
        if (element && element.offsetTop <= scrollPosition) {
          setActiveSection(sections[i]);
          break;
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const featuredProjects = [
    {
      title: "Quotes Api",
      description: "Quote-Management-API with Node.js and Express, featuring CRUD operations and React UI",
      tech: ["Node.js", "Express", "React"],
      image: apiScreenshot,
      github: "https://github.com/SpAwNnScode/quotes-api",
    },
    {
      title: "Todoo App",
      description: "Modern task management application with real-time updates and Docker deployment",
      tech: ["React", "Node.js", "PostgreSQL", "Docker"],
      image: todooScreenshot,
      github: "https://github.com/spawnnscode/todoo-app",
    },
    {
      title: "Real-Time Chat",
      description: "Interactive chat platform with real-time messaging and room management",
      tech: ["React", "Socket.io", "Node.js", "MongoDB"],
      image: chatAppScreenshot,
      github: "https://github.com/SpAwNnScode/chatApp",
    },
  ];

  const skills = [
    { name: "JavaScript", icon: <SiJavascript /> },
    { name: "Java", icon: <FaJava />,  },
    { name: "C#", icon: <SiCsharp />,  },
    { name: "Node.js", icon: <FaNodeJs />,  },
    { name: "PostgreSQL", icon: <SiPostgresql />,  },
    { name: "MongoDB", icon: <SiMongodb />,  },
    { name: "Docker", icon: <FaDocker />,  },
    { name: "React", icon: <FaReact />,  },
    { name: "Git", icon: <FaGitAlt />,  },
  ];

  return (
    <div className="home-container">
      {/* Scroll Progress Bar */}
      <motion.div 
        className="scroll-progress-bar"
        style={{ 
          scaleX: scrollProgress / 100,
          transformOrigin: "left"
        }}
      />

      {/* Section Indicator */}
      <div className="section-indicator">
        {["home", "projects", "experience", "about", "contact"].map((section) => (
          <a
            key={section}
            href={`#${section}`}
            className={`indicator-dot ${activeSection === section ? 'active' : ''}`}
            data-section={section}
            aria-label={`Navigate to ${section} section`}
          />
        ))}
      </div>

      {/* infos Section */}
      <section className="infos-section" id="home">
        <motion.div
          className="infos-content"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <motion.h1
            className="infos-title"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <span className="greeting">Hello, I'm</span>
            <div className="name-container">
              <span className="name">Ahmad Kanaan</span>
              <span className="title">Software Developer</span>
            </div>
            <div className="infos-decoration">
              <div className="code-line">const developer = {`{`}</div>
              <div className="code-line">  name: "Ahmad Kanaan",</div>
              <div className="code-line">  role: "Software Developer",</div>
              <div className="code-line">  skills: ["Node.js", "C# .NET"]</div>
              <div className="code-line">{`}`};</div>
              <div className="code-line">function createProject() {`{`}</div>
              <div className="code-line">  return innovation;</div>
              <div className="code-line">{`}`}</div>
              <div className="code-line">while(true) {`{`}</div>
              <div className="code-line">  learnNewTech();</div>
              <div className="code-line">{`}`}</div>
            </div>
          </motion.h1>

          <motion.p
            className="infos-description"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          ></motion.p>

          <motion.div
            className="infos-cta"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <Link to="/contact" className="primary-button">
              Start a Project <FaArrowRight />
            </Link>
            <a href="#projects" className="secondary-button">
              Explore My Work <FaArrowDown />
            </a>
            <a
              href="/resume.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="secondary-button"
            >
              View Resume <FaFileAlt />
            </a>
          </motion.div>

          <motion.div
            className="social-links"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
          >
            <a
              href="https://github.com/SpAwNnScode"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaGithub />
            </a>
            <a
              href="https://linkedin.com/404NotFound"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaLinkedin />
            </a>
            <a
              href="https://x.com/SpAwNnnS"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaTwitter />
            </a>
          </motion.div>
        </motion.div>

        <div className="infos-background">
          <div className="gradient-circle"></div>
          <div className="gradient-circle secondary"></div>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="projects-section">
        <motion.h2
          className="section-title"
          data-number="01."
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          Featured Projects
        </motion.h2>
        <motion.div
          className="projects-grid"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          {featuredProjects.map((project, index) => (
            <motion.div
              key={index}
              className="project-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
            >
              <div className="project-image">
                <img src={project.image} alt={project.title} />
                <div className="project-overlay">
                  <a
                    href={project.github}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="project-link"
                  >
                    View Project <FaArrowRight />
                  </a>
                </div>
              </div>
              <div className="project-content">
                <h3>{project.title}</h3>
                <p>{project.description}</p>
                <div className="project-tech">
                  {project.tech.map((tech, i) => (
                    <span key={i}>{tech}</span>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
        <motion.div
          className="view-all-projects"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <Link 
            to="/projects" 
            className="view-all-button" 
            onClick={() => window.scrollTo(0, 0)}
          >
            View All Projects <FaArrowRight />
          </Link>
        </motion.div>
      </section>

      {/* Experience Section */}
      <section id="experience" className="experience-section">
        <motion.h2
          className="section-title"
          data-number="02."
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          Professional Journey
        </motion.h2>
        <div className="timeline">
          {experienceItems.map((item, index) => (
            <motion.div
              key={index}
              className="experience-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
            >
              <img 
                src={item.logo} 
                alt="Company logo" 
                className="company-logo"
                onError={(e) => {
                  e.target.style.display = 'none';
                  console.error('Failed to load logo:', item.logo);
                }}
              />
              <h3 className="role-title">
                {item.icons} {item.title}
              </h3>
              <p className="experience-period"><strong>{item.period}</strong></p>
              <ul className="experience-list">
                {item.points.map((point, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: i * 0.1 + index * 0.3 }}
                  >
                    {point}
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="about-section">
        <motion.div
          className="about-content"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="section-title" data-number="03.">About Me</h2>
          <p className="about-text">
            I'm a developer based in Germany, holding a Bachelor's degree in Engineering Informatics from Otto von Guericke University Magdeburg. With a strong passion for modern web technologies and cloud infrastructure, I thrive in hands-on, fast-paced environments. I’m committed to continuous learning and bring a collaborative mindset along with strong communication skills to every project I take on.
          </p>
          <p className="about-text">
            
          </p>
        </motion.div>

        <motion.div
          className="skills-grid"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          {skills.map((skill, index) => (
            <motion.div
              key={index}
              className="skill-card"
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.2 }}
            >
              <div className="skill-icon">{skill.icon}</div>
              <div className="skill-info">
                <h3>{skill.name}</h3>
                <span className="skill-level">{skill.level}</span>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact-section">
        <motion.div
          className="contact-content"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="section-title" data-number="04.">Get In Touch</h2>
          <p className="contact-text">
            I'm currently open to new opportunities and collaborations. Whether
            you have a project in mind or just want to connect, I'd love to hear
            from you.
          </p>
          <Link to="/contact" className="contact-button">
            Start a Conversation <FaArrowRight />
          </Link>
        </motion.div>
      </section>
    </div>
  );
}

export default Home;
